import { useRouter } from "next/router";
import React, { FC, ReactNode, useEffect } from "react";
import { useSelector } from "react-redux";
import { isClient } from "../../utils/isClient";
import { getIsAuthInitialized, getUserRoles } from "./authSlice";
import { UserRole } from "../../generated/graphql";

interface Props {
  children?: ReactNode;
  roles: UserRole[];
}

const AuthGuard: FC<Props> = ({ roles, children }) => {
  const router = useRouter();
  const userRoles = useSelector(getUserRoles);
  // A fresh page load's saved-credentials check (_app.tsx's useEffect) is
  // guaranteed by React to run after this component's own effect below
  // (child effects fire before parent effects), so on first mount isAllowed
  // is always false regardless of whether the user actually has a saved
  // session - redirecting on that alone bounces every already-logged-in
  // user straight back to login on every fresh navigation. Waiting for
  // isInitialized sidesteps the ordering problem entirely: it's a state
  // update, which re-renders every consumer once it lands, however the
  // original effects happened to interleave.
  const isInitialized = useSelector(getIsAuthInitialized);

  const overlappedRoles = userRoles
    ? roles.filter((role) => userRoles.includes(role))
    : [];

  const isAllowed = overlappedRoles.length > 0;

  useEffect(() => {
    if (isInitialized && !isAllowed) {
      if (isClient) {
        router.replace("/auth/login/");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllowed, isInitialized]);

  if (!isInitialized || !isAllowed) {
    return null;
  }

  return <>{children}</>;
};

export { AuthGuard };
